"use client";

import { useEffect, useState } from "react";

import { GlowButton } from "@/apps/charm/components/marketing/glow-button";
import { GoldfinchLogo } from "@/packages/ui/goldfinch/logo";
import { AppLink, useAppPathname } from "@/ui/app-link/app-link";

const links = [
  { href: "/life-insurance", label: "Life Insurance" },
  { href: "/annuities", label: "Annuities" },
  { href: "/annuity/rates", label: "Compare Rates" },
  { href: "/learn", label: "Learn" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useAppPathname();

  useEffect(() => {
    function getHeroBottom(): number {
      const hero = document.querySelector("[data-dark-hero]");
      if (hero) {
        return hero.getBoundingClientRect().bottom + window.scrollY;
      }
      return window.innerHeight * 0.6;
    }

    let heroBottom = getHeroBottom();

    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      setPastHero(window.scrollY > heroBottom - 64);
    };

    const onResize = () => {
      heroBottom = getHeroBottom();
      onScroll();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const light = pastHero && scrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? light
              ? "bg-v2-bg/80 backdrop-blur-2xl border-b border-v2-border"
              : "bg-v2-black/70 backdrop-blur-2xl border-b border-v2-cream/[0.04]"
            : light
              ? "bg-v2-bg/60 backdrop-blur-sm"
              : "bg-v2-black/40 backdrop-blur-sm"
        }`}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
          <AppLink
            href="/"
            className="flex items-center gap-2 transition-opacity duration-300 hover:opacity-80">
            <GoldfinchLogo
              className={`h-8 w-auto transition-all duration-300 ${
                light ? "text-gray-900" : "text-v2-cream"
              }`}
            />
          </AppLink>

          <div className="hidden items-center gap-10 md:flex">
            {links.map((link) => (
              <AppLink
                key={link.href}
                href={link.href}
                className={`font-mono text-[11px] uppercase tracking-[3px] transition-colors duration-300 ${
                  pathname === link.href
                    ? light
                      ? "text-v2-text"
                      : "text-v2-cream"
                    : light
                      ? "text-v2-text-faint hover:text-v2-text"
                      : "text-v2-cream/80 hover:text-v2-cream"
                }`}>
                {link.label}
              </AppLink>
            ))}
          </div>

          <div className="hidden md:block">
            <GlowButton href="/quiz" variant={light ? "gold" : "ghost"}>
              Get Started
            </GlowButton>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-1.5 md:hidden"
            aria-label="Toggle menu">
            <span
              className={`h-px w-5 transition-all duration-300 ${
                light ? "bg-v2-text" : "bg-v2-cream"
              } ${menuOpen ? "translate-y-[3.5px] rotate-45" : ""}`}
            />
            <span
              className={`h-px w-5 transition-all duration-300 ${
                light ? "bg-v2-text" : "bg-v2-cream"
              } ${menuOpen ? "-translate-y-[3.5px] -rotate-45" : ""}`}
            />
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-40 flex flex-col bg-v2-black pt-24">
          <div className="flex flex-col items-center gap-8">
            {links.map((link) => (
              <AppLink
                key={link.href}
                href={link.href}
                className={`font-mono text-sm uppercase tracking-[3px] transition-colors ${
                  pathname === link.href
                    ? "text-v2-cream"
                    : "text-v2-cream/70 hover:text-v2-cream"
                }`}>
                {link.label}
              </AppLink>
            ))}
          </div>
          <div className="mt-auto p-8">
            <GlowButton href="/quiz" className="w-full text-center">
              Get Started
            </GlowButton>
          </div>
        </div>
      )}
    </>
  );
}
